import {AttachmentFile} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit/src/common/utils/testingUtils'
import {formatFloat} from '@hconnect/uikit/src/common/utils/unitFormatting'
import {HPButton} from '@hconnect/uikit/src/lib2'
import {Box, Typography, Stack} from '@mui/material'
import React from 'react'
import {type DropzoneOptions, useDropzone, ErrorCode} from 'react-dropzone'
import {useTranslation} from 'react-i18next'

const DEFAULT_MIME_TYPES = [
  'application/msword',
  'application/pdf',
  'application/rtf',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/*',
  'text/csv',
  'text/plain',
  'video/*'
]

const DEFAULT_ACCEPT_TYPES = Object.fromEntries(DEFAULT_MIME_TYPES.map((type) => [type, []]))
const DEFAULT_MAX_SIZE = 10 * 1024 * 1024
const DEFAULT_MAX_FILES = 10

type DropzoneError = {
  file: File
  errors: ErrorCode[]
}

type CustomOptions = {
  buttonText: string
  dragAndDropText: string
}

interface AttachmentUploadDropzoneProps {
  files: Array<AttachmentFile>
  setFiles: (attachments: AttachmentFile[]) => void
  options?: Pick<DropzoneOptions, 'accept' | 'maxSize' | 'maxFiles'> & Partial<CustomOptions>
  handleError?: (errors: DropzoneError[]) => void
}

export const AttachmentUploadDropzone: React.FC<AttachmentUploadDropzoneProps> = ({
  files,
  setFiles,
  options,
  handleError
}) => {
  const {
    i18n: {language}
  } = useTranslation()
  const accept = options?.accept ?? DEFAULT_ACCEPT_TYPES
  const maxSize = options?.maxSize ?? DEFAULT_MAX_SIZE
  const maxFiles = options?.maxFiles ?? DEFAULT_MAX_FILES

  const fileLimitExceeded = files.length >= maxFiles

  const {getRootProps, getInputProps, open} = useDropzone({
    accept,
    maxFiles,
    maxSize,
    // disabling click on dropzone, because we have a button to open the file dialog
    noClick: true,
    multiple: maxFiles > 1 ? true : false,
    onDrop: (acceptedFiles) => {
      if (fileLimitExceeded) {
        return handleError?.(
          acceptedFiles.map((file) => ({file, errors: [ErrorCode.TooManyFiles]}))
        )
      }
      const newFiles = acceptedFiles.map((file) => {
        const newFile: AttachmentFile = {
          id: Math.random(),
          name: file.name,
          contentType: file.type,
          preview: URL.createObjectURL(file),
          isImage: file.type.indexOf('image') >= 0,
          originalFile: file
        }

        return newFile
      })
      const mergedFiles = files.concat(newFiles)
      setFiles(mergedFiles)
    },
    onDropRejected: (rejectedFiles) => {
      handleError?.(
        rejectedFiles.map(({file, errors}) => ({
          file: file,
          errors: errors.map((error) => error.code) as ErrorCode[]
        }))
      )
    }
  })

  const allowedFileTypesCaption = Object.values(accept)
    .flatMap((extensions) => extensions)
    .join(', ')
    .toUpperCase()
  const maxFileSizeMB = `max. ${formatFloat(maxSize / (1024 * 1024), 2, language)}MB`

  return (
    <Box component="section">
      <Stack
        {...dataTestId('attachment_upload_dropzone')}
        {...getRootProps()}
        spacing={2}
        sx={{
          minHeight: '150px',
          backgroundColor: ({palette}) => palette.background.paper,
          border: ({palette}) => `2px dashed ${palette.grey[300]}`,
          borderRadius: 1,
          color: ({palette}) => palette.primary.main,
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <input {...getInputProps()} {...dataTestId('dropzone_input')} />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <HPButton
            variant="outlined"
            color="secondary"
            onClick={open}
            disabled={fileLimitExceeded}
          >
            {options?.buttonText ?? 'Upload a file'}
          </HPButton>
          <Typography variant="body2" sx={{color: ({palette}) => palette.text.primary}}>
            {options?.dragAndDropText ?? 'or drag & drop'}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{color: ({palette}) => palette.text.primary}}>
          {allowedFileTypesCaption} {maxFileSizeMB}
        </Typography>
      </Stack>
    </Box>
  )
}
